/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/10/2025 - 15:12:14
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/10/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect } from 'react';
import { Dropdown, Modal, Button, Nav, Alert } from 'react-bootstrap';
import tutorialService, { TUTORIAL_VIDEOS } from '../../api/tutorialService';
import { useTranslation } from 'react-i18next';
import { PlayFill } from 'react-bootstrap-icons';

// VideoItem interface
interface VideoItem {
  id: string;
  title: string;
  description: string;
  url: string;
}

const VideoTutorials: React.FC = () => {
  const { t } = useTranslation(['common']);
  const [showModal, setShowModal] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState<string>('tilted');
  const [videos, setVideos] = useState<VideoItem[]>(TUTORIAL_VIDEOS);
  const [videoError, setVideoError] = useState<string | null>(null);

  // Load videos when component mounts
  useEffect(() => {
    const loadVideos = async () => {
      try {
        const loadedVideos = await tutorialService.getAllTutorialVideos();
        setVideos(loadedVideos);
      } catch (error) {
        console.error("Failed to load tutorial videos:", error);
      }
    };
    
    loadVideos();
  }, []);

  const handleShowTutorial = (videoId: string) => {
    setActiveVideoId(videoId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleDontShowAgain = (videoId: string) => {
    tutorialService.markVideoAsSeen(videoId);
    setShowModal(false);
  };

  const handleVideoError = () => {
    setVideoError("There was an error loading the video. Please try again later.");
  };

  // Get the currently active video
  const activeVideo = videos.find(video => video.id === activeVideoId) || videos[0];

  return (
    <>
      {/* Tilted PV Area Tutorial */}
      <Dropdown.Item className="navDropDown mt-1">
        <div
          onClick={() => handleShowTutorial('tilted')}
          className="text-dark text-decoration-none d-flex align-items-center"
          style={{ cursor: 'pointer' }}
        >
          <div style={{ width: 20, textAlign: 'center' }}>
            <PlayFill size={16} />
          </div>
          <span className="mx-2">{t('common:tutorials.tiltedPV', 'Tilted PV Area Tutorial')}</span>
        </div>
      </Dropdown.Item>

      {/* Vertical PV Area Tutorial */}
      <Dropdown.Item className="navDropDown mt-1">
        <div
          onClick={() => handleShowTutorial('vertical')}
          className="text-dark text-decoration-none d-flex align-items-center"
          style={{ cursor: 'pointer' }}
        >
          <div style={{ width: 20, textAlign: 'center' }}>
            <PlayFill size={16} />
          </div>
          <span className="mx-2">{t('common:tutorials.verticalPV', 'Vertical PV Area Tutorial')}</span>
        </div>
      </Dropdown.Item>

      {/* Tutorial Video Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('common:tutorials.title', 'Solar Stripe Tutorials')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {videos.length > 1 && (
            <Nav variant="tabs" className="mb-3">
              {videos.map(video => (
                <Nav.Item key={video.id}>
                  <Nav.Link 
                    active={activeVideoId === video.id}
                    onClick={() => setActiveVideoId(video.id)}
                  >
                    {video.title}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          )}

          {videoError && (
            <Alert variant="warning" onClose={() => setVideoError(null)} dismissible>
              {videoError}
            </Alert>
          )}

          <div className="ratio ratio-16x9 mb-3">
            <video
              key={activeVideo.url}
              src={activeVideo.url}
              title={activeVideo.title}
              controls
              autoPlay
              className="w-100 h-100 object-fit-contain"
              onError={handleVideoError}
            />
          </div>
          
          <div className="mt-3">
            <h5>{activeVideo.title}</h5>
            <p>{activeVideo.description}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button 
            variant="outline-secondary" 
            onClick={() => handleDontShowAgain(activeVideo.id)}
          >
            {t('common:tutorials.dontShowAgain', "Don't show on dashboard")}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCloseModal}
          >
            {t('common:tutorials.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VideoTutorials;